// @workflow assistant.reply-blocks
/**
 * The AVA Assistant chart engine: the ONE place chart geometry and style decisions live.
 * Charts in chat replies and the dashboard preview both use it (ChartSvg.vue draws the result),
 * and AVA dashboards are meant to move onto it after ADIPEC, so both end up looking the same.
 *
 * The model never draws a data chart. A tool returns a spec with the real numbers, and this
 * engine turns it into plain primitives: grid lines, marks (rect / dot / path) and text labels.
 * Spec: { title, subtitle?, kind: 'bar'|'line', stacked?, categories: [], series: [{ name, color, values: [number|null] }],
 *   thresholds?: [{ label, value, tone? }], source?, empty? } (thresholds: a dashed line, e.g. a CML minimum;
 *   source and empty as on every reply block, contract "Reply blocks")
 * A bar's length starts at 0, so bars always use a 0-based axis. A line's axis is fitted to its data and
 * thresholds (lineScale), and a missing line value (null, or past the end of values) breaks the line.
 * Kind 'timeline' (a Gantt of tasks) has its own spec and builder: see timelineGeometry.js.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { SERIES_COLORS, ELLIPSIS, dataColor, widthOf, fixed } from './chartBasics.js';
import { timelineGeometry } from './timelineGeometry.js';
import { TONE } from './blockContract.js';
import { cleanLabel, cleanTone, isFiniteNumber, isPlainObject } from './blockValues.js';

export { SERIES_COLORS };
export { timelineGeometry, timelineStateColor, TIMELINE_LAYOUT, TIMELINE_STATES } from './timelineGeometry.js';

export const CHART_KIND = Object.freeze({ BAR: 'bar', LINE: 'line', TIMELINE: 'timeline' });

const BAR_PAD = Object.freeze({ left: 26, right: 6, top: 18, bottom: 22 });
const LINE_PAD = Object.freeze({ left: 26, right: 12, top: 14, bottom: 22 });
const STACK_BAR_MAX = 40;
const GROUP_BAR_MAX = 28;
const SEGMENT_GAP = 1;

export const seriesColor = (series) => dataColor(series && series.color);

/** Rounds up to an even number of steps, so the middle grid line is a whole number. */
export function niceMax(v) {
  let step = 50;
  if (v <= 10) step = 2;
  else if (v <= 30) step = 5;
  else if (v <= 100) step = 10;
  return Math.max(2 * step, Math.ceil(v / (2 * step)) * 2 * step);
}

const valueAt = (series, i) => Number((series.values || [])[i]) || 0;

/** A line point's value, or null when it is missing (null, absent, not a number): the line breaks there. */
function lineValue(series, i) {
  const raw = (series.values || [])[i];
  if (raw === null || raw === undefined || raw === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

function emptyGeometry(width, height) {
  return { viewBox: `0 0 ${width} ${height}`, height, grid: [], marks: [], labels: [], thresholds: [] };
}

/**
 * A chart's thresholds: a label, a value of 0 or more, a tone (warning when absent). Anything
 * else is left out; the chart still draws.
 * @returns {Array<{ label: string, value: number, tone: string }>}
 */
export function cleanThresholds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((t) => {
    const label = isPlainObject(t) ? cleanLabel(t.label) : null;
    const tone = label ? cleanTone(t.tone, TONE.WARNING) : undefined;
    return label && tone && isFiniteNumber(t.value) && t.value >= 0 ? [{ label, value: t.value, tone }] : [];
  });
}

/** The scale's top: the highest value or threshold, rounded up. */
const scaleMax = (peak, thresholds) => niceMax(Math.max(peak, ...thresholds.map((t) => t.value)));

const thresholdLines = (thresholds, y, pad, width) =>
  thresholds.map((t) => ({ y: fixed(y(t.value)), x1: pad.left, x2: width - pad.right, label: t.label, tone: t.tone }));

function normalise(spec) {
  const categories = Array.isArray(spec && spec.categories) ? spec.categories : [];
  const series = Array.isArray(spec && spec.series) ? spec.series : [];
  return { categories, series, title: (spec && spec.title) || 'Chart' };
}

function gridLines(ticks, y, pad, width) {
  return ticks.map((t) => ({ y: fixed(y(t)), x1: pad.left, x2: width - pad.right, label: String(t) }));
}

/** The 0-based scale bars use: 0, a whole middle line, and the rounded-up top. */
function zeroBasedScale(top) {
  const max = niceMax(top);
  return { min: 0, max, ticks: [0, max / 2, max] };
}

/** A fitted line axis: data padded by this share of the range, at most this many grid steps of 1, 2 or 5 x 10^n. */
const LINE_FIT = Object.freeze({ PAD: 0.05, MAX_STEPS: 4, MIN_STEPS: 2, STEP_FACTORS: Object.freeze([1, 2, 5]) });
/** Absorbs float noise such as 8.7 / 0.1 = 86.999… when snapping to a step. */
const SNAP_EPSILON = 1e-9;

/** The smallest 1/2/5 step that covers lo..hi in MAX_STEPS steps or fewer, and the ticks on it. */
function niceRange(lo, hi) {
  let magnitude = 10 ** Math.floor(Math.log10((hi - lo) / LINE_FIT.MAX_STEPS));
  for (;;) {
    for (const factor of LINE_FIT.STEP_FACTORS) {
      const step = factor * magnitude;
      const first = Math.floor(lo / step + SNAP_EPSILON);
      const last = Math.max(Math.ceil(hi / step - SNAP_EPSILON), first + LINE_FIT.MIN_STEPS);
      if (last - first <= LINE_FIT.MAX_STEPS) {
        const decimals = Math.max(0, -Math.floor(Math.log10(magnitude)));
        const ticks = Array.from({ length: last - first + 1 }, (_, i) => Number(((first + i) * step).toFixed(decimals)));
        return { min: ticks[0], max: ticks[ticks.length - 1], ticks };
      }
    }
    magnitude *= 10;
  }
}

/**
 * The value axis of a line chart: the one rule. Fitted to the values and the threshold lines, with
 * a little room around the data and grid lines on nice steps, so a thickness history (9.4 to 8.7 mm
 * against a 6.0 minimum and a 10.0 design) shows its slope instead of a flat line near the top.
 * It stays 0-based (as bars are) when a fit would mislead or has nothing to fit:
 * - no values, or every value and threshold equal;
 * - the lowest value or threshold is closer to 0 than the range is wide (counts like 6..19, or a
 *   range that touches 0), where cutting the axis would exaggerate the change.
 * A fitted axis is never below 0 for positive data: it is used only when the lowest point is at
 * least one range above 0, and the padding is a small share of that range.
 * @param {number[]} values the line points that are present (missing ones left out)
 * @param {Array<{ value: number }>} thresholds checked thresholds (cleanThresholds)
 * @returns {{ min: number, max: number, ticks: number[] }}
 */
export function lineScale(values, thresholds) {
  const refs = thresholds.map((t) => t.value);
  const all = [...values, ...refs];
  if (!values.length) return zeroBasedScale(Math.max(0, ...all));
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const range = hi - lo;
  if (range <= 0 || lo < range) return zeroBasedScale(Math.max(0, hi));
  const pad = range * LINE_FIT.PAD;
  return niceRange(Math.min(Math.min(...values) - pad, ...refs), Math.max(Math.max(...values) + pad, ...refs));
}

function categoryLabel(text, x, height) {
  return { x: fixed(x), y: height - 6, text: String(text), anchor: 'middle', weight: 400, tone: 'secondary' };
}

/** Rough width of one character of an 11px axis label (Figtree), to tell whether labels fit. */
const LABEL_CHAR_PX = 6;
/** Tilted labels are cut to this many characters (full name in the tooltip). */
export const ROTATED_LABEL_CHARS = 14;
const LABEL_ANGLE = -40;
/** Below this many px per category even tilted labels collide: show every nth. */
const MIN_ROTATED_SLOT = 14;

const cut = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}${ELLIPSIS}` : text);

/**
 * Category labels that never overlap: flat when they fit their slot, otherwise tilted, cut, and
 * thinned to every nth when the slots are tiny. `extra` is the height the tilted labels need under
 * the axis, added to the chart so the plot keeps its size.
 */
function axisLabels(categories, xs, slot, height, pad) {
  const names = categories.map(String);
  const longest = Math.max(...names.map((n) => n.length));
  if (longest * LABEL_CHAR_PX <= slot - 4) {
    return { labels: names.map((n, i) => ({ ...categoryLabel(n, xs[i], height), full: n })), extra: 0 };
  }
  const every = slot >= MIN_ROTATED_SLOT ? 1 : Math.ceil(MIN_ROTATED_SLOT / slot);
  const axisY = height - pad.bottom + 12;
  const labels = names.flatMap((n, i) => (i % every ? [] : [{ x: fixed(xs[i]), y: axisY, text: cut(n, ROTATED_LABEL_CHARS), full: n, anchor: 'end', rotate: LABEL_ANGLE, weight: 400, tone: 'secondary' }]));
  const shownLongest = Math.max(...labels.map((l) => l.text.length));
  const extra = Math.ceil(Math.sin((-LABEL_ANGLE * Math.PI) / 180) * shownLongest * LABEL_CHAR_PX) - pad.bottom + 16;
  return { labels, extra: Math.max(0, extra) };
}

/**
 * @param {object} spec chart spec (kind 'bar')
 * @param {{ width?: number, height?: number }} [opts] width = the chart's real pixel width, so text keeps its size
 */
export function barGeometry(spec, { width: w, height = 180 } = {}) {
  const WIDTH = widthOf(w);
  const { categories, series } = normalise(spec);
  if (!categories.length || !series.length) return emptyGeometry(WIDTH, height);
  const pad = BAR_PAD;
  const stacked = !!spec.stacked;
  const totals = categories.map((_, i) => series.reduce((sum, s) => sum + valueAt(s, i), 0));
  const peak = stacked ? Math.max(...totals) : Math.max(...series.flatMap((s) => categories.map((_, i) => valueAt(s, i))));
  const thresholds = cleanThresholds(spec.thresholds);
  const max = scaleMax(peak, thresholds);
  const y = (v) => pad.top + (height - pad.top - pad.bottom) * (1 - v / max);
  const band = (WIDTH - pad.left - pad.right) / categories.length;
  const marks = [];
  const labels = [];

  categories.forEach((category, i) => {
    const x0 = pad.left + i * band;
    if (stacked) {
      const width = Math.min(STACK_BAR_MAX, band * 0.5);
      const x = x0 + (band - width) / 2;
      let base = 0;
      series.forEach((s) => {
        const v = valueAt(s, i);
        if (!v) return;
        const top = y(base + v);
        marks.push({ kind: 'rect', x: fixed(x), y: fixed(top), w: fixed(width), h: fixed(Math.max(0, y(base) - top - SEGMENT_GAP)), color: seriesColor(s), title: `${category} · ${s.name}: ${v}` });
        base += v;
      });
      labels.push({ x: fixed(x + width / 2), y: fixed(y(base) - 6), text: String(base), anchor: 'middle', weight: 600, tone: 'primary' });
    } else {
      const width = Math.min(GROUP_BAR_MAX, (band * 0.7) / series.length);
      series.forEach((s, j) => {
        const v = valueAt(s, i);
        const x = x0 + (band - width * series.length) / 2 + j * width;
        marks.push({ kind: 'rect', x: fixed(x), y: fixed(y(v)), w: fixed(width - 2), h: fixed(y(0) - y(v)), color: seriesColor(s), title: `${category} · ${s.name}: ${v}` });
      });
    }
  });

  const axis = axisLabels(categories, categories.map((_, i) => pad.left + i * band + band / 2), band, height, pad);
  const fullHeight = height + axis.extra;
  return { viewBox: `0 0 ${WIDTH} ${fullHeight}`, height: fullHeight, grid: gridLines(zeroBasedScale(max).ticks, y, pad, WIDTH), marks, labels: [...labels, ...axis.labels], thresholds: thresholdLines(thresholds, y, pad, WIDTH) };
}

/**
 * @param {object} spec chart spec (kind 'line')
 * @param {{ width?: number, height?: number }} [opts]
 */
export function lineGeometry(spec, { width: w, height = 160 } = {}) {
  const WIDTH = widthOf(w);
  const { categories, series } = normalise(spec);
  if (!categories.length || !series.length) return emptyGeometry(WIDTH, height);
  const pad = LINE_PAD;
  const thresholds = cleanThresholds(spec.thresholds);
  const values = series.flatMap((s) => categories.map((_, i) => lineValue(s, i))).filter((v) => v !== null);
  const scale = lineScale(values, thresholds);
  const y = (v) => pad.top + (height - pad.top - pad.bottom) * (1 - (v - scale.min) / (scale.max - scale.min));
  const step = categories.length > 1 ? (WIDTH - pad.left - pad.right - 16) / (categories.length - 1) : 0;
  const x = (i) => pad.left + 8 + i * step;
  const marks = [];

  series.forEach((s) => {
    const points = categories.map((_, i) => {
      const v = lineValue(s, i);
      return v === null ? null : { x: fixed(x(i)), y: fixed(y(v)), v, i };
    });
    // A missing point ends the run; the next present point starts a new one (M), so the line has a gap.
    const d = points.map((p, i) => (p ? `${i && points[i - 1] ? 'L' : 'M'}${p.x} ${p.y}` : '')).filter(Boolean).join(' ');
    if (d) marks.push({ kind: 'path', d, color: seriesColor(s), title: s.name });
    points.forEach((p) => p && marks.push({ kind: 'dot', cx: p.x, cy: p.y, r: 3.5, color: seriesColor(s), title: `${categories[p.i]}: ${p.v}` }));
  });

  const axis = axisLabels(categories, categories.map((_, i) => x(i)), step || WIDTH - pad.left - pad.right, height, pad);
  const fullHeight = height + axis.extra;
  return { viewBox: `0 0 ${WIDTH} ${fullHeight}`, height: fullHeight, grid: gridLines(scale.ticks, y, pad, WIDTH), marks, labels: axis.labels, thresholds: thresholdLines(thresholds, y, pad, WIDTH) };
}

const GEOMETRY_BY_KIND = Object.freeze({
  [CHART_KIND.BAR]: barGeometry,
  [CHART_KIND.LINE]: lineGeometry,
  [CHART_KIND.TIMELINE]: timelineGeometry,
});

/** Picks the geometry builder for a spec's kind; an unknown kind draws as bars, as before. */
export const chartGeometry = (spec, opts) => (GEOMETRY_BY_KIND[spec && spec.kind] || barGeometry)(spec, opts);
